/**
 * Where keyboard focus goes back to when a dialog or the ☰ menu closes (QA 2026-10-21,
 * keyboard-only desktop: Esc on the sign-in modal and the /views guide dropped focus on
 * <body>, so the next Tab started over at "Skip to content").
 *
 * The rule, the same one ClipViewer and SurfaceGuide already keep inline: capture
 * document.activeElement when the dialog opens and focus it again on close, if it is
 * still in the document. One extra case they never met: the opener was a ☰ menu row,
 * and the menu unmounted the moment the row was pressed — then the ☰ button itself is
 * the way back.
 */

/** What Tab can land on — the same list ClipViewer and SurfaceGuide trap with. */
export const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The ☰ button (SiteMenu marks it), the fallback for an opener that lived in the menu. */
export const MENU_BUTTON_SELECTOR = "[data-site-menu-button]";

/** The visible focusables inside `root`, in DOM (= Tab) order. */
export function focusablesIn(root: ParentNode): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
}

/**
 * Pure: the element focus should return to. The opener while it is still connected (and
 * is not <body>, which is "nothing was focused"); else the ☰ button when the opener was a
 * row inside the now-closed menu; else nothing (leave focus where it is).
 */
export function returnFocusTarget<T extends { isConnected: boolean }>(
  opener: T | null,
  openerWasInMenu: boolean,
  menuButton: T | null,
  body: unknown = null,
): T | null {
  if (opener && opener !== body && opener.isConnected) return opener;
  if (openerWasInMenu && menuButton && menuButton.isConnected) return menuButton;
  return null;
}

/**
 * Capture the opener NOW (call it on open, before anything inside the dialog takes focus)
 * and get back the function that restores it. Safe to call during a server render.
 */
export function captureFocusReturn(): () => void {
  if (typeof document === "undefined") return () => {};
  const opener = document.activeElement as HTMLElement | null;
  const inMenu = !!opener?.closest?.('[role="menu"]');
  return () => {
    const menuButton = document.querySelector<HTMLElement>(MENU_BUTTON_SELECTOR);
    returnFocusTarget(opener, inMenu, menuButton, document.body)?.focus({ preventScroll: true });
  };
}
